import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Building2,
  Settings,
  LogOut,
  User,
  ArrowLeft,
  ShieldCheck,
  Activity,
  FolderKey,
  Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import ErrorBoundary from '../components/ErrorBoundary';

const AdminPortalLayout = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Off-canvas sidebar drawer below md (mobile); fixed sidebar at md+.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Close the drawer on ANY route change (programmatic navigate / browser
  // back-forward), not just NavLink clicks, so the backdrop can never stick open.
  useEffect(() => { setMobileSidebarOpen(false); }, [location.pathname]);
  // Track "below md" (Tailwind md = 768px) so the closed off-canvas drawer can
  // be `inert` on mobile — exact match to md: via max-width 767px, avoiding a
  // 768px (iPad-portrait) off-by-one where the fixed desktop sidebar is visible.
  const [belowMd, setBelowMd] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setBelowMd(e.matches);
    setBelowMd(mql.matches);
    // Safari <14 only supports the deprecated add/removeListener API.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  // Accessible drawer (below md): Escape closes it so keyboard users aren't
  // trapped behind the inert <main>, and opening moves focus INTO the drawer
  // (the toggle that opened it lives in the now-inert <main>, so focus would
  // otherwise be lost to <body>).
  const asideRef = useRef(null);
  useEffect(() => {
    if (!belowMd || !mobileSidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    asideRef.current?.querySelector('a, button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [belowMd, mobileSidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatRole = (role) => {
    if (!role) return '';
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Overview', end: true },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/projects', icon: FolderKey, label: 'Projects' },
    ...(isSuperAdmin() ? [{ to: '/admin/organizations', icon: Building2, label: 'Organizations' }] : []),
    { to: '/admin/activity-log', icon: Activity, label: 'Activity Log' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile drawer backdrop — only below md, only when the drawer is open */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="admin-sidebar-backdrop"
          aria-hidden="true"
        />
      )}
      {/* Sidebar - Dark theme for admin portal; off-canvas drawer below md.
          When closed below md the drawer is `inert` so its off-screen links
          leave the keyboard tab order — and unlike `visibility:hidden` this
          preserves the slide transition (inert doesn't affect rendering). */}
      <aside
        ref={asideRef}
        inert={belowMd && !mobileSidebarOpen ? '' : undefined}
        className={`fixed left-0 top-0 z-50 h-screen w-64 border-r border-slate-700 bg-slate-900 flex flex-col transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        {/* Logo Header */}
        <div className="flex h-14 items-center px-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-slate-700 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold tracking-tight text-white">
                epicplan
              </h1>
              <p className="text-[10px] text-slate-400 -mt-0.5">Admin Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4 px-3">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Separator className="bg-slate-700 my-4" />

          {/* Switch to App link */}
          <NavLink
            to="/dashboard"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Switch to App
          </NavLink>
        </ScrollArea>

        {/* User section at bottom */}
        <div className="border-t border-slate-700 p-3">
          <div className="flex items-center gap-3 mb-2 px-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700">
              <User className="h-4 w-4 text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name}
              </p>
              <p className="text-[10px] text-slate-400 truncate">
                {formatRole(user?.role)}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-slate-400 hover:text-white hover:bg-slate-800 h-8"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main content. When the drawer is open below md, the page sits behind the
          backdrop — `inert` keeps keyboard focus confined to the open drawer. */}
      <main className="ml-0 md:ml-64" inert={belowMd && mobileSidebarOpen ? '' : undefined}>
        {/* Top header */}
        <header className="sticky top-0 z-30 h-14 border-b border-slate-200 bg-white">
          <div className="flex h-full items-center justify-between gap-2 px-3 sm:px-6">
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden h-9 w-9 p-0"
                onClick={() => setMobileSidebarOpen(true)}
                data-testid="admin-sidebar-toggle"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5 text-slate-600" />
              </Button>
              <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium">
                Admin
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 shrink-0">
                {formatRole(user?.role)}
              </span>
              <span className="text-sm text-slate-500 truncate min-w-0">
                {user?.email}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 sm:p-6">
          <ErrorBoundary variant="section" name="admin-page" resetKeys={[location.pathname]}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
};

export default AdminPortalLayout;
